import { removeVietnameseTones } from '@/utils/common';
import {
  InfoCircleOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { css } from '@emotion/react';
import {
  Button,
  Col,
  Collapse,
  CollapseProps,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Tooltip,
  Typography
} from 'antd';

type DishSliderSearcherProps = {
  onSearch: (values: FilterDish) => void;
  listDishSalt: DishSalt[];
  listDishAge: DishAge[];
  listDishCookingMethod: DishCookingMethod[];
  listSourceDish: ListSourceDish[];
  listStructureMealDish: StructureMealDish[];
  listRegionDish: RegionDish[];
  listTagDish: TagDish[];
};

export const DishManagementSearcher = ({
  onSearch,
  listDishSalt,
  listDishAge,
  listDishCookingMethod,
  listSourceDish,
  listStructureMealDish,
  listRegionDish,
  listTagDish,
}: DishSliderSearcherProps) => {
  const [form] = Form.useForm();
  const startDate = Form.useWatch('update_from_date', form);
  const endDate = Form.useWatch('update_to_date', form);

  const saltDishGroupOptions = listDishSalt.map((item) => ({
    value: item.formulaType,
    label: item.formulaType
  }));

  const ageDishGroupOptions = listDishAge.map((item) => ({
    value: item.id,
    label: item.id === 'DT13'
      ? 'Từ trên 19 tuổi (tức 19 tuổi 1 ngày)- dưới 31 tuổi'
      : item.name
  }));

  const dishCookingMethodGroupOptions = listDishCookingMethod.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const sourceDishGroupOptions = listSourceDish.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const structureMealDishGroupOptions = listStructureMealDish.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const regionDishGroupOptions = listRegionDish.map((item) => ({
    value: item.regionId,
    label: item.regionName
  }));

  const tagDishGroupOptions = listTagDish.map((item) => ({
    value: item.tagName,
    label: item.tagName
  }));

  const handleSearch = () => {
    const values = form.getFieldsValue();
    onSearch(values);
  }

  const handleClear = () => {
    form.resetFields();
    onSearch({})
  };

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: (
        <Typography.Title css={titleStyles} level={5}>
          BỘ LỌC
        </Typography.Title>
      ),
      children: (
        <Form form={form} css={formStyles}>
          <Row gutter={16} align='stretch'>
            <Col span={8} css={filterItem}>
              <Form.Item
                label='Tên món ăn'
                name='name'
                css={customLabel}
                labelCol={{ span: 8 }}
                colon={false}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8} css={filterItem}>
              <Form.Item
                label='Phân loại nguồn món ăn'
                name='sourceDish'
                css={customLabel}
                labelCol={{ span: 8 }}
                colon={false}
              >
                <Select
                  mode='multiple'
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  filterOption={(input, option) =>
                    removeVietnameseTones((option?.label ?? '').toString().toLowerCase())
                      .includes(removeVietnameseTones(input.toLowerCase()))
                  }
                  options={sourceDishGroupOptions} />
              </Form.Item>
            </Col>
            <Col span={8} css={filterItem}>
              <Form.Item
                label='Nhóm món ăn theo cấu trúc bữa ăn'
                name='structureMealDish'
                css={customLabel}
                labelCol={{ span: 8 }}
                colon={false}
              >
                <Select
                  mode='multiple'
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  filterOption={(input, option) =>
                    removeVietnameseTones((option?.label ?? '').toString().toLowerCase())
                      .includes(removeVietnameseTones(input.toLowerCase()))
                  }
                  options={structureMealDishGroupOptions} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} align='stretch'>
            <Col span={8} css={filterItem}>
              <Form.Item
                label='Nhóm món ăn theo phương pháp nấu'
                name='dishCookingMethod'
                css={customLabel}
                labelCol={{ span: 8 }}
                colon={false}
              >
                <Select
                  mode='multiple'
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  filterOption={(input, option) =>
                    removeVietnameseTones((option?.label ?? '').toString().toLowerCase())
                      .includes(removeVietnameseTones(input.toLowerCase()))
                  }
                  options={dishCookingMethodGroupOptions} />
              </Form.Item>
            </Col>
            <Col span={8} css={filterItem}>
              <Form.Item
                label='Độ tuổi'
                name='dishAge'
                css={customLabel}
                labelCol={{ span: 8 }}
                colon={false}
              >
                <Select
                  mode='multiple'
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  filterOption={(input, option) =>
                    removeVietnameseTones((option?.label ?? '').toString().toLowerCase())
                      .includes(removeVietnameseTones(input.toLowerCase()))
                  }
                  options={ageDishGroupOptions} />
              </Form.Item>
            </Col>
            <Col span={8} css={filterItem}>
              <Form.Item
                label='Nhãn món ăn'
                name='dishLabel'
                css={customLabel}
                labelCol={{ span: 8 }}
                colon={false}
              >
                <Select
                  mode='multiple'
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  filterOption={(input, option) => {
                    if (!tagDishGroupOptions?.length) return false;
                    if (!input) return true;

                    const inputNormalized = removeVietnameseTones(input.trim().toLowerCase());
                    const label = (option?.label ?? '').toString();
                    const labelNormalized = removeVietnameseTones(label.toLowerCase());

                    return (
                      label.toLowerCase().includes(input.toLowerCase()) ||
                      labelNormalized.includes(inputNormalized)
                    );
                  }}
                  options={tagDishGroupOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} align='middle' style={{ marginTop: '1rem' }}>
            <Col span={8} css={filterItem}>
              <Form.Item
                label='Miền'
                name='region'
                css={customLabel}
                labelCol={{ span: 8 }}
                colon={false}
              >
                <Select
                  mode='multiple'
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  filterOption={(input, option) =>
                    removeVietnameseTones((option?.label ?? '').toString().toLowerCase())
                      .includes(removeVietnameseTones(input.toLowerCase()))
                  }
                  options={regionDishGroupOptions}
                />
              </Form.Item>
            </Col>
            <Col span={8} css={filterItem}>
              <Form.Item
                label={
                  <>
                    Công thức muối&ensp;
                    <Tooltip
                      title={
                        <Typography>
                          Lượng muối trong các công thức muối tuân theo quy luật
                          như sau: CTM1&lt;CTM2&lt;CTM3&lt;CTM4&lt;CTM5
                        </Typography>
                      }
                      color='white'
                    >
                      <InfoCircleOutlined css={infoIconStyle} />
                    </Tooltip>
                  </>
                }
                name='saltRecipe'
                css={customLabel}
                labelCol={{ span: 8 }}
                colon={false}
              >
                <Select
                  mode='multiple'
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  filterOption={(input, option) => {
                    if (!saltDishGroupOptions?.length) return false;

                    if (!input) return true;

                    const inputNormalized = removeVietnameseTones(input.trim().toLowerCase());
                    const label = (option?.label ?? '').toString();
                    const labelNormalized = removeVietnameseTones(label.toLowerCase());

                    return (
                      label.toLowerCase().startsWith(input.toLowerCase()) ||
                      labelNormalized.startsWith(inputNormalized)
                    );
                  }}

                  options={saltDishGroupOptions}
                />
              </Form.Item>
            </Col>
            <Col span={8} css={[timeStyle, filterItem]}>
              <Form.Item
                label='Thời gian cập nhật'
                style={{ width: "100%" }}
                css={customLabel}
                labelCol={{ span: 8 }}
                colon={false}
              >
                <Row gutter={8} style={{ width: "100%", alignItems: "center", justifyContent: "center" }}>
                  <Col span={11}>
                    <Form.Item name='update_from_date' style={{ width: "100%" }}>
                      <DatePicker
                        format="DD/MM/YYYY"
                        style={{ width: "100%" }}
                        disabledDate={(current) =>
                          endDate ? current && current > endDate.endOf('day') : false
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={2} style={{ textAlign: "center" }}> - </Col>
                  <Col span={11}>
                    <Form.Item name='update_to_date' style={{ width: "100%" }} >
                      <DatePicker
                        format="DD/MM/YYYY"
                        style={{ width: "100%" }}
                        disabledDate={(current) =>
                          startDate ? current && current < startDate.startOf('day') : false
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
          </Row>

          <Row
            gutter={16}
            align='middle'
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '1rem'
            }}
          >
            <Button
              type='default'
              icon={<ReloadOutlined />}
              css={resetButton}
              onClick={handleClear}
            >
              Đặt lại
            </Button>
            <Button
              type='primary'
              icon={<SearchOutlined />}
              css={searchButton}
              onClick={handleSearch}
            >
              Tìm kiếm
            </Button>
          </Row>
        </Form>
      )
    }
  ];
  return (
    <Row css={boxStyles} justify='space-between' align='middle'>
      <Collapse
        items={items}
        defaultActiveKey={'1'}
        expandIconPosition={'end'}
        css={collapseStyles}
      />
    </Row>
  );
};

const boxStyles = css`
  padding: 0 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const collapseStyles = css`
  width: 100%;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
  background-color: transparent !important;

  .ant-collapse-header {
    width: fit-content;
    border-radius: 0.8rem !important;
    border: none !important;
    padding: 0 !important;

    .ant-collapse-expand-icon {
      align-self: center;
    }
  }

  .ant-collapse-content-box {
    padding: 0 !important;
  }

  .ant-collapse-content {
    border: none !important;
  }
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  line-height: 3.2rem !important;
  font-weight: 700 !important;
`;

const formStyles = css`
  margin-top: 0.5rem;
  padding: 1rem;
  gap: 1rem;
`;

const filterItem = css`
  display: flex;
  justify-content: start !important;
  gap: 0.5rem;

  label {
    height: 100% !important;
  }

  .ant-form-item-label {
    width: fit-content;
    max-width: 33.33%;
    height: 100% !important;
    text-align: start !important;
    white-space: normal;
    font-size: 14px;
    font-weight: 500;
    word-wrap: break-word;
    word-break: break-word;
  }

  input,
  .ant-select,
  .ant-picker {
    flex: 1;
    min-width: 6rem;
  }
`;

const customLabel = css`
  .ant-form-item-label > label {
    font-weight: normal !important;
  }

  width: 100%;
  margin-bottom: 0;
`;

const resetButton = css`
  margin-right: 10px;
  color: #007bff;
  border-color: #007bff;
`;

const searchButton = css`
  background: #007bff;
  color: white;
  &:hover {
    background: #0056b3;
  }
`;

const infoIconStyle = css`
  color: var(--success-color) !important;
`;

const timeStyle = css`
  .ant-form-item {
    margin-bottom: unset !important;
  }
   .ant-row {
    margin-left: unset !important;
    margin-right: unset !important;
  }
  .ant-col .ant-col-11 {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }  
`
